import React from 'react'
import ReactDOM from 'react-dom/client'

// Import bundled Inter font (all weights for offline support)
import '@fontsource/inter/100.css'
import '@fontsource/inter/200.css'
import '@fontsource/inter/300.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/inter/800.css'
import '@fontsource/inter/900.css'

import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import App from './App'
import './index.css'
import { initializePlatform, isIOS, isNative } from './lib/capacitor'
import { migrateCompanyStorageIfNeeded } from './lib/session'

/**
 * Restore pinch-zoom on native iOS, and mark <html> so index.css can apply a 16px font floor
 * to inputs. The two must ship together: iOS auto-zooms the page whenever a focused input is
 * under 16px, and this app still has ~21 inputs at text-sm/text-xs. Enabling zoom without the
 * floor trades a WCAG failure for a worse everyday bug.
 *
 * Runs before render so the swap is never visible.
 *
 * TODO(android-zoom): Android is deliberately left on the restrictive viewport from index.html.
 * Play Store has no equivalent rejection risk, and turning zoom on there would also bump those
 * same inputs to 16px, a visible resize of existing production screens for no user-facing win.
 * If Android should match iOS: delete the branch below so it runs for all native platforms,
 * drop the html.is-ios qualifier on the input rule in index.css, and re-check the dense list
 * rows (JobsPage, InvoicesPage, EstimatesPage filter bars) for overflow at the larger size.
 *
 * TODO(ios-web): this keys on Capacitor's platform, so iPhone users on the *web* app keep the
 * restrictive tag. Mobile Safari has ignored user-scalable=no since iOS 10, so those users can
 * already pinch-zoom AND already get focus-zoom on the sub-16px inputs today. Extending the
 * `is-ios` class to iOS Safari would fix that, but it changes web UI, so it needs its own call.
 */
function applyIosViewportZoom(): void {
  if (!isIOS()) return;

  document.documentElement.classList.add('is-ios');
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover');
}

applyIosViewportZoom();

// Initialize platform-specific settings
initializePlatform();

// Register the service worker on web only (precaches the app shell for offline
// page loads). Skipped on native — Capacitor serves the shell locally and Capgo
// owns bundle updates there.
if (!isNative()) {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({ immediate: true })
  })
}

// One-time migration: merge legacy selectedCompanyId + companyTimezone keys into
// the new atomic selectedCompanyData key so timezone is never scoped to the wrong company.
void migrateCompanyStorageIfNeeded();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)

